import { useState, useEffect } from 'react';
import { getToken, getSession } from '../api';

const API = process.env.REACT_APP_API_URL || '';

export default function BuilderFarm({ navigate }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [builds, setBuilds] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [checklist, setChecklist] = useState(null);
  const session = getSession();

  // Load generic prompt on mount
  useEffect(() => {
    fetch(`${API}/v1/farm`).then(r => r.json()).then(data => {
      setPrompt(data.prompt);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Load builder's builds if logged in
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API}/v1/builder/builds`, {
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(r => r.json()).then(data => {
      if (data.builds) setBuilds(data.builds);
    }).catch(() => {});
  }, []);

  // Load build-specific prompt + checklist when selection changes
  useEffect(() => {
    if (!selectedSlug) return;
    const token = getToken();
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/v1/builder/builds/${selectedSlug}/farm`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json()),
    ]).then(([farmData]) => {
      setPrompt(farmData.prompt);
      setChecklist(farmData.checklist);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedSlug]);

  function copyPrompt() {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Builder Farm</h1>
        <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
          Get an AI-generated system prompt tailored to help you create, improve, and publish quality builds on invokeAPI.
          Paste it into Claude, ChatGPT, or any AI assistant — it will guide you through the entire process.
        </p>
      </div>

      {/* Build selector for logged-in builders */}
      {session && builds.length > 0 && (
        <div style={{ marginBottom: 24, padding: 16, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>
            Customize for an existing build:
          </label>
          <select
            value={selectedSlug}
            onChange={e => setSelectedSlug(e.target.value)}
            style={{
              padding: '8px 12px', fontSize: 14, borderRadius: 6,
              border: '1px solid var(--border)', background: 'var(--bg)',
              color: 'var(--fg)', width: '100%', maxWidth: 400,
            }}
          >
            <option value="">New build (generic prompt)</option>
            {builds.map(b => (
              <option key={b.slug} value={b.slug}>
                {b.name} ({b.slug}) — {b.is_live ? 'Live' : 'Offline'}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Checklist */}
      {checklist && (
        <div style={{
          marginBottom: 24, padding: 16, background: 'var(--card-bg)',
          border: `1px solid ${checklist.ready ? 'var(--success, #22c55e)' : 'var(--warning, #f59e0b)'}`,
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 20 }}>{checklist.ready ? '\u2705' : '\u26a0\ufe0f'}</span>
            <strong style={{ fontSize: 15 }}>
              Publish Readiness: {checklist.score}% ({checklist.passed}/{checklist.total})
            </strong>
          </div>
          {checklist.blockers?.length > 0 && (
            <div style={{ marginBottom: 12, padding: 8, background: 'rgba(239,68,68,0.1)', borderRadius: 6, fontSize: 13 }}>
              <strong>Blockers:</strong>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {checklist.blockers.map((b, i) => <li key={i} style={{ color: '#ef4444' }}>{b}</li>)}
              </ul>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 4 }}>
            {checklist.checks?.map((c, i) => (
              <div key={i} style={{ fontSize: 13, color: c.done ? 'var(--muted)' : 'var(--fg)' }}>
                <span style={{ marginRight: 6 }}>{c.done ? '\u2705' : (c.critical ? '\u274c' : '\u25cb')}</span>
                {c.label}
                {c.critical && !c.done && <span style={{ color: '#ef4444', marginLeft: 4, fontSize: 11 }}>(required)</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div style={{
        marginBottom: 24, padding: 16, background: 'var(--card-bg)',
        border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>How it works</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <Step n="1" title="Copy the prompt" desc="Click the copy button below to get the full system prompt" />
          <Step n="2" title="Paste into your AI" desc="Open Claude, ChatGPT, or any AI assistant and paste it as the first message" />
          <Step n="3" title="Follow the guide" desc="The AI will walk you through creating your endpoint, registering tools, running scans, and going live" />
        </div>
      </div>

      {/* Prompt */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>
            System Prompt {selectedSlug ? `(for ${selectedSlug})` : '(generic)'}
          </span>
          <button
            onClick={copyPrompt}
            style={{
              padding: '6px 16px', fontSize: 13, fontWeight: 600,
              background: copied ? 'var(--success, #22c55e)' : 'var(--accent)',
              color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
            }}
          >
            {copied ? 'Copied!' : 'Copy Prompt'}
          </button>
        </div>
        <pre style={{
          padding: 16, background: '#1a1a2e', color: '#e0e0e0',
          borderRadius: 8, fontSize: 12, lineHeight: 1.5,
          maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap',
          wordBreak: 'break-word', border: '1px solid var(--border)',
        }}>
          {loading ? 'Loading...' : prompt}
        </pre>
      </div>

      {/* CTA */}
      {!session && (
        <div style={{ marginTop: 24, textAlign: 'center', padding: 24, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <p style={{ fontSize: 14, marginBottom: 12 }}>Sign in to get a customized prompt for your existing builds, with scan results and a publish-readiness checklist.</p>
          <button
            onClick={() => navigate('signin')}
            style={{
              padding: '10px 24px', fontSize: 14, fontWeight: 600,
              background: 'var(--accent)', color: '#fff', border: 'none',
              borderRadius: 8, cursor: 'pointer',
            }}
          >
            Sign In
          </button>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)',
          color: '#fff', fontSize: 12, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{n}</span>
        <strong style={{ fontSize: 14 }}>{title}</strong>
      </div>
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, paddingLeft: 32 }}>{desc}</p>
    </div>
  );
}
